/**
 * SchemaDesignerPanel — a FORWARD-ONLY visual schema designer.
 *
 * Design new tables (columns, keys, foreign keys) and get correct CREATE TABLE
 * DDL live (web/src/lib/ddlgen.js — pure, node-tested, FK-dependency ordered).
 * Deliberately forward-only: it emits CREATE for new objects and never tries to
 * diff/ALTER an existing populated schema (the data-preserving, rename-detecting
 * migration is the part that can't be done correctly without guesswork). Copy the
 * DDL or open it in a real query tab to review and run.
 */
import { createSignal, createMemo, createResource, createEffect, For, Show } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { api } from '../lib/api.js';
import { buildCreateDDL, COMMON_TYPES, FK_ACTIONS } from '../lib/ddlgen.js';
import { toast } from '../lib/toast.js';

const newColumn = () => ({ name: '', type: 'INT', nullable: true, autoInc: false, primaryKey: false, default: '' });
const newTable = (n) => ({ name: `table${n}`, columns: [{ name: 'id', type: 'BIGINT', nullable: false, autoInc: true, primaryKey: true, default: '' }], foreignKeys: [] });

export default function SchemaDesignerPanel(props) {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal('');
  const [dbList, setDbList] = createSignal([]);
  const [database, setDatabase] = createSignal('');
  createEffect(() => {
    const id = connId();
    if (!id) { setDbList([]); return; }
    api.get(`/api/connections/${id}/databases`).then((r) => setDbList((r.databases || []).map((d) => d.name))).catch(() => setDbList([]));
  });
  const connObj = () => (conns() || []).find((c) => String(c.id) === String(connId())) || null;

  const [model, setModel] = createStore({ tables: [], engine: 'InnoDB', charset: 'utf8mb4', ifNotExists: false });
  let tableSeq = 0;

  const addTable = () => { setModel('tables', (t) => [...t, newTable(++tableSeq)]); };
  const removeTable = (i) => setModel('tables', (t) => t.filter((_, k) => k !== i));
  const setTableName = (i, v) => setModel('tables', i, 'name', v);

  const addColumn = (ti) => setModel('tables', ti, 'columns', (c) => [...c, newColumn()]);
  const removeColumn = (ti, ci) => setModel('tables', ti, 'columns', (c) => c.filter((_, k) => k !== ci));
  const setCol = (ti, ci, key, v) => setModel('tables', ti, 'columns', ci, key, v);

  const addFK = (ti) => setModel('tables', ti, 'foreignKeys', (f) => [...f, { columns: [''], refTable: '', refColumns: [''], onDelete: '', onUpdate: '' }]);
  const removeFK = (ti, fi) => setModel('tables', ti, 'foreignKeys', (f) => f.filter((_, k) => k !== fi));
  const setFK = (ti, fi, key, v) => setModel('tables', ti, 'foreignKeys', produce((fks) => {
    if (key === 'col') fks[fi].columns = [v];
    else if (key === 'refCol') fks[fi].refColumns = [v];
    else fks[fi][key] = v;
  }));

  const columnsOf = (tableName) => {
    const t = model.tables.find((x) => x.name === tableName);
    return t ? t.columns.filter((c) => c.name) : [];
  };

  const generated = createMemo(() => buildCreateDDL(model));

  const copyDDL = async () => {
    const sql = generated().sql;
    if (!sql) return;
    try { await navigator.clipboard.writeText(sql); toast.success('DDL copied'); } catch { toast.error('Copy failed'); }
  };
  const openInTab = () => {
    const sql = generated().sql;
    const c = connObj();
    if (!sql) { toast.error('Design at least one table'); return; }
    if (!c) { toast.error('Pick a connection to open a query tab'); return; }
    props.onOpenQuery?.(c, database(), sql);
  };

  return (
    <div class="sd-panel">
      <div class="sd-toolbar">
        <select class="input sd-sel" value={connId()} onChange={(e) => { setConnId(e.currentTarget.value); setDatabase(''); }}>
          <option value="">— connection (for Open in tab) —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <select class="input sd-sel" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)} disabled={!dbList().length}>
          <option value="">— database —</option>
          <For each={dbList()}>{(n) => <option value={n}>{n}</option>}</For>
        </select>
        <span class="qb-divider" />
        <button class="btn btn-sm btn-primary" onClick={addTable}>＋ Table</button>
        <label class="sd-opt"><input type="checkbox" checked={model.ifNotExists} onChange={(e) => setModel('ifNotExists', e.currentTarget.checked)} /> IF NOT EXISTS</label>
        <label class="sd-opt">Charset
          <input class="input sd-mini" value={model.charset} onInput={(e) => setModel('charset', e.currentTarget.value)} />
        </label>
        <span class="spacer" />
        <button class="btn btn-sm" onClick={copyDDL} disabled={!generated().sql}>⧉ Copy DDL</button>
        <button class="btn btn-sm" onClick={openInTab} disabled={!generated().sql}>↗ Open in query tab</button>
      </div>

      <div class="sd-body">
        <div class="sd-tables">
          <Show when={model.tables.length === 0}>
            <div class="sd-empty">Design tables here. Add a table to start — its CREATE TABLE DDL builds live on the right. Forward-only: this emits CREATE for new objects (no ALTER of existing data).</div>
          </Show>
          <For each={model.tables}>
            {(t, ti) => (
              <div class="sd-table">
                <div class="sd-table-head">
                  <input class="input sd-tname" value={t.name} onInput={(e) => setTableName(ti(), e.currentTarget.value)} placeholder="table_name" />
                  <span class="spacer" />
                  <button class="qb-mini" title="Remove table" onClick={() => removeTable(ti())}>× table</button>
                </div>

                {/* Columns */}
                <table class="sd-cols">
                  <thead><tr><th>Column</th><th>Type</th><th title="Primary key">PK</th><th title="NOT NULL">NN</th><th title="AUTO_INCREMENT">AI</th><th>Default</th><th></th></tr></thead>
                  <tbody>
                    <For each={t.columns}>
                      {(c, ci) => (
                        <tr>
                          <td><input class="input sd-cell" value={c.name} onInput={(e) => setCol(ti(), ci(), 'name', e.currentTarget.value)} placeholder="name" /></td>
                          <td>
                            <input class="input sd-cell sd-type" list="sd-types" value={c.type} onInput={(e) => setCol(ti(), ci(), 'type', e.currentTarget.value)} />
                          </td>
                          <td class="sd-ck"><input type="checkbox" checked={c.primaryKey} onChange={(e) => setCol(ti(), ci(), 'primaryKey', e.currentTarget.checked)} /></td>
                          <td class="sd-ck"><input type="checkbox" checked={!c.nullable} onChange={(e) => setCol(ti(), ci(), 'nullable', !e.currentTarget.checked)} /></td>
                          <td class="sd-ck"><input type="checkbox" checked={c.autoInc} onChange={(e) => setCol(ti(), ci(), 'autoInc', e.currentTarget.checked)} /></td>
                          <td><input class="input sd-cell" value={c.default} onInput={(e) => setCol(ti(), ci(), 'default', e.currentTarget.value)} placeholder="—" /></td>
                          <td><button class="qb-mini" title="Remove column" onClick={() => removeColumn(ti(), ci())}>×</button></td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
                <button class="qb-mini sd-add" onClick={() => addColumn(ti())}>＋ column</button>

                {/* Foreign keys */}
                <div class="sd-fks">
                  <For each={t.foreignKeys}>
                    {(fk, fi) => (
                      <div class="sd-fk">
                        <span class="sd-fk-lbl">FK</span>
                        <select class="input sd-mini-sel" value={fk.columns[0] || ''} onChange={(e) => setFK(ti(), fi(), 'col', e.currentTarget.value)}>
                          <option value="">column…</option>
                          <For each={t.columns.filter((c) => c.name)}>{(c) => <option value={c.name}>{c.name}</option>}</For>
                        </select>
                        <span class="sd-fk-arrow">→</span>
                        <select class="input sd-mini-sel" value={fk.refTable} onChange={(e) => setFK(ti(), fi(), 'refTable', e.currentTarget.value)}>
                          <option value="">table…</option>
                          <For each={model.tables.filter((x) => x.name)}>{(x) => <option value={x.name}>{x.name}</option>}</For>
                        </select>
                        <select class="input sd-mini-sel" value={fk.refColumns[0] || ''} onChange={(e) => setFK(ti(), fi(), 'refCol', e.currentTarget.value)}>
                          <option value="">col…</option>
                          <For each={columnsOf(fk.refTable)}>{(c) => <option value={c.name}>{c.name}</option>}</For>
                        </select>
                        <select class="input sd-mini-sel" value={fk.onDelete} onChange={(e) => setFK(ti(), fi(), 'onDelete', e.currentTarget.value)} title="ON DELETE">
                          <For each={FK_ACTIONS}>{(a) => <option value={a}>{a ? 'DEL ' + a : 'on delete…'}</option>}</For>
                        </select>
                        <button class="qb-mini" title="Remove foreign key" onClick={() => removeFK(ti(), fi())}>×</button>
                      </div>
                    )}
                  </For>
                  <button class="qb-mini sd-add" onClick={() => addFK(ti())}>＋ foreign key</button>
                </div>
              </div>
            )}
          </For>
        </div>

        {/* DDL preview */}
        <div class="sd-preview">
          <div class="sd-preview-h">CREATE DDL
            <Show when={generated().warnings.length}>
              <span class="qb-warn" title={generated().warnings.join('\n')}>⚠ {generated().warnings.length}</span>
            </Show>
          </div>
          <pre class="sd-sql">{generated().sql || '-- add a table to begin'}</pre>
        </div>
      </div>

      <datalist id="sd-types"><For each={COMMON_TYPES}>{(t) => <option value={t} />}</For></datalist>
    </div>
  );
}
